import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useDisclosure } from "@chakra-ui/react";
import { FiPlus, FiTrash2, FiUser } from "react-icons/fi";

import { Button } from "newui/components/Button";
import { useUnitsAndMeasures } from "newui/store";
import { cn } from "utils/helpers";
import { formatCurrency } from "../../../components/QuoteTable/utils";
import { DeleteRowModal } from "../../../components/QuoteTable/DeleteRowModal";
import type { QuoteTableColumn, SupplierRow } from "../../../components/QuoteTable/quote-table.types";
import { CELL_BORDER, COLUMN_STYLE } from "../../../components/QuoteTable/quote-table.types";
import {
	createSupplierRow,
	deleteSupplierRow,
	updateSupplierRow,
} from "../../api";
import { CompactTextInput } from "newui/components/ui/InputWithLabel";

export interface SupplierRowsSectionHandle {
	validate: () => boolean;
	/**
	 * Persist any pending supplier rows (rows still living only in local state
	 * because the description-textarea blur hasn't fired). Parents MUST await
	 * this before submitting the quote form, otherwise unsaved rows are lost
	 * on refresh — the click on a submit button races the blur handler.
	 */
	flushPending: () => Promise<void>;
}

export interface SupplierRowsSectionProps {
	columns?: QuoteTableColumn[];
	disabled: boolean;
	initialRows?: SupplierRow[];
	inviteId: number;
	isClientView: boolean;
	quoteId: number;
	showZeroColumn?: boolean;
	startIndex: number;
	onFormikFieldChange?: (itemId: number, field: string, value: number | string) => void;
	onRowsChange?: (rows: SupplierRow[]) => void;
}

const SUPPLIER_ROW_BG =
	"bg-blue-100/30 group-hover:bg-blue-200 group-focus-within:bg-blue-300";

/**
 * Build the cells payload for a supplier-created row's structural fields —
 * the primary description (always sent, even if empty) plus any additional
 * description columns the supplier filled in. Response cells (rate / qty /
 * etc.) continue to flow via the answer submission path.
 */
const buildSupplierRowCells = (
	row: SupplierRow,
	sortedColumns: QuoteTableColumn[],
	primaryDescriptionColId: number | null,
): { column_id: number; value: string | null }[] => {
	const cells: { column_id: number; value: string | null }[] = [];
	if (primaryDescriptionColId !== null) {
		cells.push({ column_id: primaryDescriptionColId, value: row.description ?? "" });
	}
	if (row.columnValues) {
		for (const col of sortedColumns) {
			if (col.columnType === "description" && col.id !== primaryDescriptionColId) {
				const val = row.columnValues[col.id];
				if (val !== undefined) {
					cells.push({ column_id: col.id, value: String(val) });
				}
			}
		}
	}
	return cells;
};

const SupplierRowsSection = forwardRef<SupplierRowsSectionHandle, SupplierRowsSectionProps>(({
	columns = [],
	disabled,
	inviteId,
	isClientView,
	quoteId,
	initialRows = [],
	onRowsChange,
	onFormikFieldChange,
	showZeroColumn = false,
	startIndex,
}, ref) => {
	const nextLocalIdRef = useRef(1);
	const generateLocalId = (): string => {
		const id = `supplier-row-${nextLocalIdRef.current}`;
		nextLocalIdRef.current += 1;
		return id;
	};

	const [rows, setRows] = useState<SupplierRow[]>(initialRows);
	const { values: unitsAndMeasures } = useUnitsAndMeasures();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);
	const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());

	const sortedColumns = useMemo(
		() => [...columns].sort((a, b) => a.sortOrder - b.sortOrder),
		[columns]
	);

	const primaryDescriptionColId = useMemo(() => {
		return sortedColumns.find((c) => c.columnType === "description")?.id ?? null;
	}, [sortedColumns]);

	// Total column count for colSpan: zero column + # column + dynamic columns + optional delete action
	const totalColSpan = useMemo(() => {
		return (showZeroColumn ? 1 : 0) + 1 + sortedColumns.length;
	}, [sortedColumns, showZeroColumn]);

	useImperativeHandle(ref, () => ({
		flushPending: async (): Promise<void> => {
			// POST any rows still living only in local state. Without this, a
			// click on the parent form's Save button can race the description-
			// textarea blur (which is what normally triggers the create) and
			// the row never reaches the DB.
			const pending = rows.filter(
				(r) => !r.savedId && r.description.trim() !== ""
			);
			for (const row of pending) {
				try {
					const savedRow = await createSupplierRow(
						{ inviteId, quoteId },
						{
							unit_id: row.unitId,
							cells: buildSupplierRowCells(
								row,
								sortedColumns,
								primaryDescriptionColId
							),
						}
					);
					setRows((prev) =>
						prev.map((r) =>
							r.localId === row.localId
								? { ...r, savedId: savedRow.id }
								: r
						)
					);
					// Push numeric/text column values into Formik now that the
					// row finally has a DB id — the answer-submission pipeline
					// uses savedId as the formik key.
					if (onFormikFieldChange && row.columnValues) {
						for (const col of sortedColumns) {
							const val = row.columnValues[col.id];
							if (val === undefined || val === "") {
								continue;
							}
							if (col.columnType === "response_value") {
								const num = parseFloat(val);
								if (!isNaN(num) && num >= 0) {
									onFormikFieldChange(savedRow.id, `rate_${col.id}`, num);
								}
							} else if (col.columnType === "saving") {
								const num = parseFloat(val);
								if (!isNaN(num)) {
									onFormikFieldChange(savedRow.id, `rate_${col.id}`, num);
								}
							} else if (col.columnType === "response_quantity") {
								const num = parseFloat(val);
								if (!isNaN(num) && num >= 0) {
									onFormikFieldChange(savedRow.id, "quantity", num);
								}
							} else if (col.columnType === "response_text") {
								onFormikFieldChange(savedRow.id, `text_${col.id}`, val);
							} else if (col.columnType === "response_number") {
								const num = parseFloat(val);
								if (!isNaN(num) && num >= 0) {
									onFormikFieldChange(savedRow.id, `number_${col.id}`, num);
								}
							}
						}
					}
				} catch {
					// Error handled by global interceptor; surface via toast.
				}
			}
		},
		validate: (): boolean => {
			if (rows.length === 0) {
				return true;
			}
			const errors = new Set<string>();
			for (const row of rows) {
				// Validate based on actual columns present
				for (const col of sortedColumns) {
					if (col.columnType === "description" && col.id === primaryDescriptionColId) {
						if (!row.description.trim()) {
							errors.add(`${row.localId}-description`);
						}
					} else if (col.columnType === "unit_of_measurement") {
						if (row.unitId === null || row.unitId === undefined) {
							errors.add(`${row.localId}-unitId`);
						}
					} else if (col.columnType === "response_quantity") {
						const val = row.columnValues?.[col.id];
						if (val === undefined || val === null || String(val).trim() === "") {
							errors.add(`${row.localId}-qty_${col.id}`);
						}
					} else if (col.columnType === "response_value") {
						const val = row.columnValues?.[col.id];
						if (val === undefined || val === null || String(val).trim() === "") {
							errors.add(`${row.localId}-rate_${col.id}`);
						}
					}
				}
			}
			if (errors.size > 0) {
				setValidationErrors(errors);
				return false;
			}
			setValidationErrors(new Set());
			return true;
		},
	}), [rows, sortedColumns, primaryDescriptionColId, inviteId, quoteId, onFormikFieldChange]);

	const updateRows = useCallback(
		(newRows: SupplierRow[]) => {
			setRows(newRows);
			onRowsChange?.(newRows);
		},
		[onRowsChange]
	);

	const handleAddRow = useCallback(() => {
		const newRow: SupplierRow = {
			localId: generateLocalId(),
			description: "",
			unitId: null,
			quantity: "",
			rate: "",
		};
		updateRows([...rows, newRow]);
	}, [rows, updateRows]);

	const handleFieldChange = useCallback(
		(
			localId: string,
			field: keyof SupplierRow,
			value: string | number | null
		) => {
			const updatedRows = rows.map((row) => {
				if (row.localId === localId) {
					return { ...row, [field]: value };
				}
				return row;
			});
			updateRows(updatedRows);

			if (value && String(value).trim()) {
				setValidationErrors((prev) => {
					const next = new Set(prev);
					next.delete(`${localId}-${field}`);
					return next;
				});
			}
		},
		[rows, updateRows]
	);

	const handleColumnValueChange = useCallback(
		(localId: string, columnId: number, value: string) => {
			const updatedRows = rows.map((row) => {
				if (row.localId === localId) {
					return {
						...row,
						columnValues: {
							...(row.columnValues ?? {}),
							[columnId]: value,
						},
					};
				}
				return row;
			});
			updateRows(updatedRows);

			// Clear validation error for this field
			setValidationErrors((prev) => {
				const next = new Set(prev);
				// Clear any matching column validation key
				for (const key of prev) {
					if (key.startsWith(`${localId}-`) && key.endsWith(`_${columnId}`)) {
						next.delete(key);
					}
				}
				return next;
			});

			// Sync numeric column values to Formik
			if (onFormikFieldChange) {
				const row = rows.find((r) => r.localId === localId);
				if (row?.savedId) {
					const col = sortedColumns.find((c) => c.id === columnId);
					if (col?.columnType === "response_value") {
						const numericValue = parseFloat(value);
						onFormikFieldChange(
							row.savedId,
							`rate_${columnId}`,
							isNaN(numericValue) || numericValue < 0 ? "" : numericValue
						);
					} else if (col?.columnType === "response_quantity") {
						const numericValue = parseFloat(value);
						onFormikFieldChange(
							row.savedId,
							"quantity",
							isNaN(numericValue) || numericValue < 0 ? "" : numericValue
						);
					} else if (col?.columnType === "response_text") {
						onFormikFieldChange(row.savedId, `text_${columnId}`, value);
					} else if (col?.columnType === "saving") {
						// `value` arrives already negated (the saving cell pre-negates so
						// the stored figure matches procurer-row semantics: negative =
						// discount). Pass straight through; the < 0 guard used for
						// response_value would wrongly reject it here.
						const numericValue = parseFloat(value);
						onFormikFieldChange(
							row.savedId,
							`rate_${columnId}`,
							isNaN(numericValue) ? "" : numericValue
						);
					} else if (col?.columnType === "response_number") {
						const numericValue = parseFloat(value);
						onFormikFieldChange(
							row.savedId,
							`number_${columnId}`,
							isNaN(numericValue) || numericValue < 0 ? "" : numericValue
						);
					}
				}
			}
		},
		[rows, updateRows, onFormikFieldChange, sortedColumns]
	);

	const handleSaveRow = useCallback(
		async (localId: string) => {
			const row = rows.find((r) => r.localId === localId);
			if (!row) {
				return;
			}

			if (row.savedId) {
				try {
					await updateSupplierRow(
						{ inviteId, quoteId },
						row.savedId,
						{ unit_id: row.unitId, cells: buildSupplierRowCells(row, sortedColumns, primaryDescriptionColId) }
					);
				} catch {
					// Error handled by global interceptor
				}
				return;
			}

			// For new rows, don't create until description is provided
			if (!row.description.trim()) {
				return;
			}

			try {
				const savedRow = await createSupplierRow(
					{ inviteId, quoteId },
					{ unit_id: row.unitId, cells: buildSupplierRowCells(row, sortedColumns, primaryDescriptionColId) }
				);
				updateRows(
					rows.map((r) => {
						if (r.localId === localId) {
							return { ...r, savedId: savedRow.id };
						}
						return r;
					})
				);

				if (onFormikFieldChange && row.columnValues) {
					for (const col of sortedColumns) {
						const val = row.columnValues[col.id];
						if (val === undefined || val === "") {
							continue;
						}
						if (col.columnType === "response_value") {
							const num = parseFloat(val);
							if (!isNaN(num) && num >= 0) {
								onFormikFieldChange(savedRow.id, `rate_${col.id}`, num);
							}
						} else if (col.columnType === "response_quantity") {
							const num = parseFloat(val);
							if (!isNaN(num) && num >= 0) {
								onFormikFieldChange(savedRow.id, "quantity", num);
							}
						} else if (col.columnType === "response_text") {
							onFormikFieldChange(savedRow.id, `text_${col.id}`, val);
						} else if (col.columnType === "response_number") {
							const num = parseFloat(val);
							if (!isNaN(num) && num >= 0) {
								onFormikFieldChange(savedRow.id, `number_${col.id}`, num);
							}
						} else if (col.columnType === "saving") {
							// Saving values are pre-negated in row.columnValues so they
							// flow into Formik with the discount sign intact (matches
							// the per-cell handler in handleColumnValueChange).
							const num = parseFloat(val);
							if (!isNaN(num)) {
								onFormikFieldChange(savedRow.id, `rate_${col.id}`, num);
							}
						}
					}
				}
			} catch {
				// Error handled by global interceptor
			}
		},
		[rows, inviteId, quoteId, updateRows, onFormikFieldChange, sortedColumns, primaryDescriptionColId]
	);

	const handleDeleteClick = useCallback(
		(localId: string) => {
			setDeleteTarget(localId);
			onOpen();
		},
		[onOpen]
	);

	const handleConfirmDelete = useCallback(async () => {
		if (!deleteTarget) {
			return;
		}

		const row = rows.find((r) => r.localId === deleteTarget);
		if (!row) {
			onClose();
			return;
		}

		setIsDeleting(true);
		try {
			if (row.savedId) {
				await deleteSupplierRow(
					{ inviteId, quoteId },
					row.savedId
				);
			}
			updateRows(rows.filter((r) => r.localId !== deleteTarget));
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsDeleting(false);
			setDeleteTarget(null);
			onClose();
		}
	}, [deleteTarget, rows, inviteId, quoteId, updateRows, onClose]);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "-") {
			event.preventDefault();
		}
	};

	const getUnitLabel = (unitId: number | null): string => {
		if (unitId === null) {
			return "-";
		}
		return (
			unitsAndMeasures?.find((u) => u.value === unitId)?.label ?? "-"
		);
	};

	const handlePaste = useCallback(
		(event: React.ClipboardEvent<HTMLTextAreaElement>, localId: string, colId: number) => {
			const text = event.clipboardData.getData("text");
			if (!text.includes("\t") && !text.includes("\n")) {
				return; // Single-cell paste — let default happen
			}
			event.preventDefault();

			const matrix = text
				.replace(/\r\n/g, "\n")
				.replace(/\r/g, "\n")
				.split("\n")
				.filter((line, idx, arr) => !(idx === arr.length - 1 && line === ""))
				.map((line) => line.split("\t"));

			if (matrix.length === 0) {
				return;
			}

			const startColIndex = sortedColumns.findIndex((c) => c.id === colId);
			if (startColIndex === -1) {
				return;
			}

			const buildRowValues = (pastedRow: string[]): { description: string; columnValues: Record<number, string> } => {
				let description = "";
				const colVals: Record<number, string> = {};
				for (let dx = 0; dx < pastedRow.length; dx++) {
					const destCol = sortedColumns[startColIndex + dx];
					if (!destCol) {
						break;
					}
					const val = pastedRow[dx];
					if (destCol.columnType === "description" && destCol.id === primaryDescriptionColId) {
						description = val;
					} else {
						colVals[destCol.id] = val;
					}
				}
				return { description, columnValues: colVals };
			};

			// Find the index of the pasted-into row
			const pasteIdx = rows.findIndex((r) => r.localId === localId);

			// Helper: is a row considered empty (no description, no column values)?
			const isRowEmpty = (r: SupplierRow): boolean =>
				!r.description.trim() &&
				!r.savedId &&
				(!r.columnValues || Object.values(r.columnValues).every((v) => !v || !String(v).trim()));

			// First pasted line updates the target row
			const firstRowData = buildRowValues(matrix[0]);
			const result = rows.map((r) => {
				if (r.localId === localId) {
					return {
						...r,
						description: firstRowData.description || r.description,
						columnValues: { ...(r.columnValues ?? {}), ...firstRowData.columnValues },
					};
				}
				return r;
			});

			// Remaining pasted lines fill empty rows after the target, or insert new ones
			const extraLines = matrix.slice(1);
			let insertAt = pasteIdx + 1; // where the next row should go

			for (const pastedRow of extraLines) {
				const data = buildRowValues(pastedRow);
				const newRow: SupplierRow = {
					localId: generateLocalId(),
					description: data.description,
					unitId: null,
					quantity: "",
					rate: "",
					columnValues: Object.keys(data.columnValues).length > 0 ? data.columnValues : undefined,
				};

				// If there's an empty row at the insert position, overwrite it
				if (insertAt < result.length && isRowEmpty(result[insertAt])) {
					result[insertAt] = {
						...result[insertAt],
						description: data.description || result[insertAt].description,
						columnValues: { ...(result[insertAt].columnValues ?? {}), ...data.columnValues },
					};
				} else {
					// Insert a new row at this position
					result.splice(insertAt, 0, newRow);
				}
				insertAt += 1;
			}

			updateRows(result);
		},
		[rows, sortedColumns, primaryDescriptionColId, updateRows]
	);

	const renderSupplierCell = (row: SupplierRow, col: QuoteTableColumn): React.ReactNode => {
		const isPrimary = col.id === primaryDescriptionColId;

		if (col.columnType === "description") {
			if (isPrimary) {
				if (disabled || isClientView) {
					return (
						<span className="pl-2 font-roboto text-sm text-gray-900 whitespace-pre-wrap break-words">
							{row.description || "-"}
						</span>
					);
				}
				return (
					<textarea
						className={cn(
							"w-full resize-none overflow-hidden rounded border bg-white px-2 py-1 text-sm",
							validationErrors.has(`${row.localId}-description`) ? "border-red-500" : "border-gray-300"
						)}
						rows={1}
						value={row.description}
						onChange={(e) => {
							e.target.style.height = "auto";
							e.target.style.height = `${e.target.scrollHeight}px`;
							handleFieldChange(row.localId, "description", e.target.value);
						}}
						onFocus={(e) => {
							e.target.style.height = "auto";
							e.target.style.height = `${e.target.scrollHeight}px`;
						}}
						onBlur={() => handleSaveRow(row.localId)}
						onPaste={(e) => handlePaste(e, row.localId, col.id)}
						placeholder="Enter scope description"
						data-testid={`supplier-row-desc-${row.localId}`}
					/>
				);
			}
			// Additional description columns
			const value = row.columnValues?.[col.id] ?? "";
			if (disabled || isClientView) {
				return <span className="pl-2 font-roboto text-sm text-gray-900 whitespace-pre-wrap break-words">{value || "-"}</span>;
			}
			return (
				<textarea
					className="w-full resize-none overflow-hidden rounded border border-gray-300 bg-white px-2 py-1 text-sm"
					rows={1}
					value={value}
					onChange={(e) => {
						e.target.style.height = "auto";
						e.target.style.height = `${e.target.scrollHeight}px`;
						handleColumnValueChange(row.localId, col.id, e.target.value);
					}}
					onFocus={(e) => {
						e.target.style.height = "auto";
						e.target.style.height = `${e.target.scrollHeight}px`;
					}}
					placeholder="Enter description"
					data-testid={`supplier-desc-col-${col.id}-${row.localId}`}
				/>
			);
		}

		if (col.columnType === "unit_of_measurement") {
			if (disabled || isClientView) {
				return (
					<span className="pl-2 font-roboto text-sm text-gray-900">
						{getUnitLabel(row.unitId)}
					</span>
				);
			}
			return (
				<select
					className={cn(
						"w-full rounded border bg-white px-2 py-1 text-sm",
						validationErrors.has(`${row.localId}-unitId`) ? "border-red-500" : "border-gray-300"
					)}
					value={row.unitId ?? ""}
					onChange={(e) =>
						handleFieldChange(
							row.localId,
							"unitId",
							e.target.value ? Number(e.target.value) : null
						)
					}
					onBlur={() => handleSaveRow(row.localId)}
					data-testid={`supplier-row-unit-${row.localId}`}
				>
					<option value="">Select unit</option>
					{unitsAndMeasures?.map((unit) => (
						<option key={unit.value} value={unit.value}>
							{unit.label}
						</option>
					))}
				</select>
			);
		}

		if (col.columnType === "response_quantity") {
			const value = row.columnValues?.[col.id] ?? "";
			if (disabled || isClientView) {
				return <span className="pl-2 font-roboto text-sm text-gray-900">{value || "-"}</span>;
			}
			return (
				<CompactTextInput
					type="number"
					hasError={validationErrors.has(`${row.localId}-qty_${col.id}`)}
					value={value}
					onChange={(e) =>
						handleColumnValueChange(row.localId, col.id, e.target.value)
					}
					onKeyDown={handleKeyDown}
					min={0}
					disabled={disabled || isClientView}
					placeholder="0"
					data-testid={`supplier-row-qty-${col.id}-${row.localId}`}
					required
				/>
			);
		}

		if (col.columnType === "response_value") {
			const value = row.columnValues?.[col.id] ?? "";
			if (disabled || isClientView) {
				return <span className="pl-2 font-roboto text-sm text-gray-900">{value ? formatCurrency(Number(value)) : "-"}</span>;
			}
			return (
				<div className="relative flex">
					<span className="absolute left-2 top-1/2 z-10 -translate-y-1/2 text-sm text-gray-500">$</span>
					<CompactTextInput
						type="number"
						value={value}
						onChange={(e) =>
							handleColumnValueChange(row.localId, col.id, e.target.value)
						}
						hasError={validationErrors.has(`${row.localId}-rate_${col.id}`)}
						inputClassName="pl-7"
						onKeyDown={handleKeyDown}
						min={0}
						step={1}
						disabled={disabled || isClientView}
						placeholder="0"
						data-testid={`supplier-row-rate-${col.id}-${row.localId}`}
						required
					/>
				</div>
			);
		}

		if (col.columnType === "saving") {
			// Stored as a negative number (supplier types the positive amount, we
			// negate on commit) to mirror procurer-row behaviour in QuoteTableCell.
			const rawValue = row.columnValues?.[col.id] ?? "";
			const storedNum = rawValue === "" ? null : Number(rawValue);
			if (disabled || isClientView) {
				return (
					<span className="font-roboto text-sm font-semibold text-red-600">
						{storedNum === null || isNaN(storedNum)
							? "-"
							: formatCurrency(storedNum)}
					</span>
				);
			}
			const displayValue =
				storedNum === null || isNaN(storedNum)
					? ""
					: String(Math.abs(storedNum));
			return (
				<div className="relative flex">
					<span className="absolute left-2 top-1/2 z-10 -translate-y-1/2 text-sm text-red-600">
						-$
					</span>
					<CompactTextInput
						type="number"
						inputClassName="pl-7"
						value={displayValue}
						onChange={(e) => {
							const numericValue = parseFloat(e.target.value);
							if (isNaN(numericValue) || numericValue < 0) {
								handleColumnValueChange(row.localId, col.id, "");
								return;
							}
							const negated =
								numericValue === 0 ? "0" : String(-Math.abs(numericValue));
							handleColumnValueChange(row.localId, col.id, negated);
						}}
						hasError={validationErrors.has(`${row.localId}-rate_${col.id}`)}
						onKeyDown={handleKeyDown}
						min={0}
						step={1}
						disabled={disabled || isClientView}
						placeholder="0"
						data-testid={`supplier-row-saving-${col.id}-${row.localId}`}
						required
					/>
				</div>
			);
		}

		if (col.columnType === "response_text") {
			const value = row.columnValues?.[col.id] ?? "";
			if (disabled || isClientView) {
				return <span className="pl-2 font-roboto text-sm text-gray-900 whitespace-pre-wrap break-words">{value || "-"}</span>;
			}
			return (
				<textarea
					className="w-full resize-none overflow-hidden rounded border border-gray-300 bg-white px-2 py-1 text-sm"
					rows={1}
					value={value}
					onChange={(e) => {
						e.target.style.height = "auto";
						e.target.style.height = `${e.target.scrollHeight}px`;
						handleColumnValueChange(row.localId, col.id, e.target.value);
					}}
					onFocus={(e) => {
						e.target.style.height = "auto";
						e.target.style.height = `${e.target.scrollHeight}px`;
					}}
					placeholder="Enter response"
					data-testid={`supplier-resp-col-${col.id}-${row.localId}`}
				/>
			);
		}

		if (col.columnType === "response_number") {
			const value = row.columnValues?.[col.id] ?? "";
			return (
				<CompactTextInput
					type="number"
					className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm"
					value={value}
					onChange={(e) => handleColumnValueChange(row.localId, col.id, e.target.value)}
					onKeyDown={handleKeyDown}
					min={0}
					disabled={disabled || isClientView}
					placeholder="0"
					data-testid={`supplier-num-col-${col.id}-${row.localId}`}
				/>
			);
		}

		if (col.columnType === "total") {
			// Sum all response_value column values for this row
			const valueColIds = sortedColumns
				.filter((c) => c.columnType === "response_value")
				.map((c) => c.id);
			const total = valueColIds.reduce((sum, colId) => {
				const val = Number(row.columnValues?.[colId] ?? 0);
				return sum + val;
			}, 0);
			return (
				<span className="block pr-2 text-right font-roboto text-sm text-gray-900">
					{formatCurrency(isNaN(total) ? 0 : total)}
				</span>
			);
		}

		return <span className="text-gray-400">-</span>;
	};

	if (isClientView && rows.length === 0) {
		return null;
	}

	// Determine if the last column should contain the delete action
	const lastColumnId = sortedColumns.length > 0 ? sortedColumns[sortedColumns.length - 1].id : null;

	return (
		<>
			{rows.length > 0 && (
				<tbody data-testid="supplier-rows-section">
					{rows.map((row, idx) => (
						<tr
							key={row.localId}
							className={cn("group border-b border-[#EAECF0]", SUPPLIER_ROW_BG)}
							data-testid={`supplier-row-${row.localId}`}
						>
							{showZeroColumn && (
								<td className={cn(CELL_BORDER, SUPPLIER_ROW_BG)} style={{ width: "5%" }} />
							)}
							<td
								className={cn(
									CELL_BORDER,
									"text-center font-roboto text-xs font-normal text-primary-500",
									SUPPLIER_ROW_BG
								)}
								style={{ height: "35px" }}
							>
								<div className="flex items-center justify-center gap-1">
									<span>{startIndex + idx + 1}</span>
									<FiUser
										className="text-blue-500"
										size={10}
										title="Supplier-added row"
									/>
								</div>
							</td>
							{sortedColumns.map((col) => (
								<td
									key={col.id}
									className={cn(
										CELL_BORDER,
										COLUMN_STYLE,
										SUPPLIER_ROW_BG
									)}
								>
									<div className="flex items-center justify-between">
										<div className="flex-1">
											{renderSupplierCell(row, col)}
										</div>
										{col.id === lastColumnId && !disabled && !isClientView && (
											<button
												type="button"
												className="ml-2 shrink-0 rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600"
												onClick={() => handleDeleteClick(row.localId)}
												title="Remove row"
												data-testid={`supplier-row-delete-${row.localId}`}
											>
												<FiTrash2 size={14} />
											</button>
										)}
									</div>
								</td>
							))}
						</tr>
					))}
				</tbody>
			)}

			{!disabled && !isClientView && (
				<tfoot>
					<tr>
						<td
							colSpan={totalColSpan}
							className="px-4 py-2"
						>
							<Button
								size="xs"
								btntype="secondary"
								onClick={handleAddRow}
								data-testid="add-supplier-row-btn"
							>
								<FiPlus className="mr-1" />
								Add Row
							</Button>
						</td>
					</tr>
				</tfoot>
			)}

			<DeleteRowModal
				isOpen={isOpen}
				isDeleting={isDeleting}
				onClose={onClose}
				onConfirm={handleConfirmDelete}
			/>
		</>
	);
});

SupplierRowsSection.displayName = "SupplierRowsSection";

export { SupplierRowsSection };
